import ReportDocumentHeader from '@/components/Reports/ReportPrivate/_comons/ReportDocumentHeader';
import ErrorFallback from '@/components/_common/ErrorFallback';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import ReportTable, {
  ReportTableColumn,
} from '@/components/_common/TemplateReport/componentes/ReportTable';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import { useInventoryReport } from '@/core/domain/reportData/inventory/Report.hooks';
import {
  InventoryFilterByEnum,
  InventoryOrderByEnum,
  InventoryReportResponse,
} from '@/core/domain/reportData/inventory/Report.types';
import { useQueryParams } from '@/hooks/useQueryParams';
import { RegistrationsDefaultCode } from '@/types/Register';
import { Space } from 'antd';
import { Suspense, lazy } from 'react';
import { useRouteMatch } from 'react-router-dom';

const TemplateReport = lazy(
  () => import('@/components/_common/TemplateReport/TemplateReport'),
);

export default function PrivateInventory() {
  const match = useRouteMatch();
  const params = useQueryParams<{
    inventoryId: string;
    barCode: string;
    stock: string;
    oldCount: string;
    currentCount: string;
    filterBy: string;
    orderBy: string;
    fromCode: string;
    toCode: string;
    fromLocation: string;
    toLocation: string;
    groups: string;
    company: string;
  }>();
  const { data: matrix } = useMatrix();
  document.title = 'Relatório de inventário';
  const { filterBy, orderBy } = params;

  const inventoryReport = useInventoryReport({
    id: +params.inventoryId,
    filterBy: +filterBy as InventoryFilterByEnum,
    orderBy: +orderBy as InventoryOrderByEnum,
    codeStart: params.fromCode,
    codeEnd: params.toCode,
    groupIds: params.groups,
    localizationStart: params.fromLocation,
    localizationEnd: params.toLocation,
  });

  const itemPartCode = (item: InventoryReportResponse) => {
    const itemPartCodeObj = {
      [RegistrationsDefaultCode.barCode]: item.stockItemsDto.barCode,
      [RegistrationsDefaultCode.companyCode]: item.stockItemsDto.companyCode,
      [RegistrationsDefaultCode.internalCode]: item.stockItemsDto.sku,
      [RegistrationsDefaultCode.manufacturerCode]:
        item.stockItemsDto.manufacturingCode,
      [RegistrationsDefaultCode.sku]: item.stockItemsDto.sku,
    };
    const matrixPartCode = matrix?.matrixSettings.defaultPartCode;

    return (
      <p>
        {
          itemPartCodeObj[
            matrixPartCode || RegistrationsDefaultCode.manufacturerCode
          ]
        }
      </p>
    );
  };

  const columns: ReportTableColumn<InventoryReportResponse>[] = [
    {
      title: <p>Código</p>,
      render: itemPartCode,
    },
    {
      title: <p>Descrição</p>,
      render: (row) => <p>{row.productDto.name}</p>,
    },
    {
      title: <p>Localização</p>,
      render: (item) => <p>{item.stockItemsDto.location}</p>,
    },
    params.stock === 'true'
      ? {
          title: <p>Estoque</p>,
          render: (item) => (
            <p>{item.stockItemsDto.totalStock?.quantity || 0}</p>
          ),
        }
      : {},
    params.oldCount === 'true'
      ? {
          title: <p>Contagem anterior</p>,
          render: (item) => <p>{item.item?.oldQuantity || 0}</p>,
        }
      : {},
    params.currentCount === 'true'
      ? {
          title: <p>Contagem atual</p>,
          render: (item) => <p>{item.item?.countedQuantity || 0}</p>,
        }
      : {},
    {
      width: '7%',
      title: <p>Quantidade Contada</p>,
      render: () => (
        <div
          style={{
            marginBottom: '0px',
            paddingBottom: '0px',
            borderBottom: '1px solid gray',
          }}
        >
           
        </div>
      ),
    },
    params.barCode === 'true'
      ? {
          title: <p>Código de barras</p>,
          render: (item) => (
            <div style={{ whiteSpace: 'break-spaces' }}>
              <p>{item.stockItemsDto.barCode}</p>
            </div>
          ),
        }
      : {},
  ];

  return (
    <Suspense fallback={<span>Carregando...</span>}>
      {inventoryReport.isLoading && (
        <ModalLoadingOverlay
          messages={[
            'Conferindo dados de inventário...',
            'Gerando relatório detalhado...',
            'Finalizando formatação para impressão...',
            'Carregando...',
          ]}
        />
      )}

      {inventoryReport.isError && (
        <ErrorFallback
          backHref={`/app/movements/inventory/${params.inventoryId}`}
        />
      )}

      {match.isExact && !inventoryReport.isLoading && !!inventoryReport.data && (
        <TemplateReport>
          <main>
            <Space direction="vertical" size="large" className="w-100">
              <ReportDocumentHeader
                title="Relatório de inventário"
                company={{
                  name: params.company,
                }}
              />
              <ReportTable
                columns={columns}
                dataSource={inventoryReport.data || []}
                className="table-break-word"
              />
            </Space>
          </main>
        </TemplateReport>
      )}
    </Suspense>
  );
}
